import cx from 'classnames';
import CustomRoutes from 'components/custom-routes/CustomRoutes';
import PageActions from 'components/page-actions/PageActions';
import TimerText from 'components/timer-text/TimerText';
import Typography from 'components/typography/Typography';
import { TYPOGRAPHY_VARIANTS } from 'components/typography/typography-utils';
import { I18nNamespace } from 'constants/i18n.const';
import { FRAME_TO_PAYMENT_DIRECTLY } from 'constants/photo.const';
import { SelfeRouteConst } from 'constants/route.const';
import { useBoothAppContext } from 'context/BoothAppContext';
import { isHigher } from 'helpers/math.helper';
import { usePageTimer } from 'hooks/usePageTimer';
import { useRouter } from 'hooks/useRouter';
import { compact, map } from 'lodash';
import { SelfeAppStateModel } from 'models/app.model';
import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useAppSelector } from 'store/store-hooks';
import { BoothStateModel } from '../../../models/photo/booth.model';
import AddFilter from '../add-filter/AddFilter';
import SelectBackground from '../select-background/Select-background';
import SelectSticker from '../select-sticker/Select-sticker';
import SelectTheme from '../select-theme/SelectTheme';
import './edit-photo.css';
import { useEditPhotoActions } from './useEditPhotoActions';

const EditPhoto = () => {
  const { t } = useTranslation([I18nNamespace.COMMON, I18nNamespace.PAGE]);
  const { isMatchPath } = useRouter();
  const navigate = useNavigate();
  const { handleNext, handleBack } = useEditPhotoActions();
  const { second } = usePageTimer({
    forwardFunc: () => handleNext?.(),
  });

  const { frameId } = useAppSelector(
    (state: { selfeApp: SelfeAppStateModel }) => state.selfeApp,
  );
  const { currentLayout } = useBoothAppContext();
  const {
    isTurnOnSticker,
    isTurnOnBackground,
    isVerticalScreen,
    isThemeBeforePayment,
    isEventBooth,
  } = useAppSelector((state: { booth: BoothStateModel }) => state.booth);

  // show background page
  const isShowBackground = useMemo(
    () => !isEventBooth && isTurnOnBackground,
    [isEventBooth, isTurnOnBackground],
  );
  // show sticker page
  const isShowSticker = useMemo(
    () =>
      !isEventBooth && isTurnOnSticker && frameId !== FRAME_TO_PAYMENT_DIRECTLY,
    [frameId, isEventBooth, isTurnOnSticker],
  );
  // show theme page
  const isShowTheme = useMemo(
    () => !isThemeBeforePayment,
    [isThemeBeforePayment],
  );

  const routes = React.useMemo(
    () =>
      compact([
        isShowTheme && {
          path: SelfeRouteConst.THEME,
          element: <SelectTheme />,
          title: t(`${I18nNamespace.COMMON}:theme`),
        },
        {
          path: SelfeRouteConst.ADD_FILTER,
          element: <AddFilter />,
          title: t(`${I18nNamespace.COMMON}:editColor`),
        },
        isShowSticker && {
          path: SelfeRouteConst.STICKER,
          element: <SelectSticker />,
          title: t(`${I18nNamespace.COMMON}:selectSticker`),
        },
        isShowBackground && {
          path: SelfeRouteConst.BACKGROUND,
          element: <SelectBackground />,
          title: t(`${I18nNamespace.COMMON}:background`),
        },
      ]),
    [isShowBackground, isShowSticker, isShowTheme, t],
  );

  const renderEditPhotoActions = () => (
    <PageActions
      className={cx({ 'edit-photo__page-actions--vertical': isVerticalScreen })}
      NextButtonProps={{ children: t('page:getImage') }}
      handleBack={handleBack}
      handleContinue={handleNext}
    />
  );

  return (
    <>
      <div
        className={cx('edit-photo', {
          'edit-photo__vertical-frame': isHigher(
            currentLayout?.height,
            currentLayout?.width,
          ),
          'edit-photo__frame-26':
            currentLayout?.code && currentLayout?.code?.indexOf('26') === 0,
        })}
      >
        <TimerText second={second} />
        <div className="page-title-wrapper edit-photo__title">
          {map(routes, (route) => {
            const isPageActive = isMatchPath(route?.path);

            return (
              <button type="button" onClick={() => navigate(route?.path)}>
                <Typography
                  variant={TYPOGRAPHY_VARIANTS.H1}
                  className={cx({
                    'edit-photo__title--inactive': !isPageActive,
                  })}
                >
                  {route?.title}
                </Typography>
              </button>
            );
          })}
        </div>
        {isVerticalScreen && renderEditPhotoActions()}
        <CustomRoutes routes={routes} outerRoute={SelfeRouteConst.EDIT_PHOTO} />
      </div>
      {!isVerticalScreen && renderEditPhotoActions()}
    </>
  );
};
export default EditPhoto;
